import { Box, Stack, Typography } from "@mui/material"
import { axisClasses, LineChart, MarkElement } from "@mui/x-charts-pro"
import type { MarkElementProps } from "@mui/x-charts-pro"
import type React from "react"
import { useCallback, useMemo, useState } from "react"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { DrawerChartTitle } from "./precomputedDrawerStyles"
import type { NwssSignalSample } from "./nwssSampleTypes"
import { NwssRollingRateHoverPopper } from "./NwssRollingRateTooltip"
import {
  formatNwssRollingRateChartTitle,
  nwssRollingChartYAxisLabel,
  type NwssDrawerSampleScope,
} from "./nwssDisplayFormat"
import {
  computeRollingCountYAxis,
  computeRollingRateYAxis,
  NWSS_DRAWER_CHART_LOOKBACK_MONTHS,
  type NwssRollingDetectionRateData,
} from "./nwssWeeklyCharts"

export interface NwssRollingDetectionRateChartProps {
  panels: NwssRollingDetectionRateData[]
  samples: NwssSignalSample[]
  disease?: string
  truncated?: boolean
  reportingCounties?: string[] | null
  lookbackMonths?: number
  sampleScope?: NwssDrawerSampleScope
}

export const NwssRollingDetectionRateChart: React.FC<
  NwssRollingDetectionRateChartProps
> = ({
  panels,
  samples,
  disease,
  truncated = false,
  reportingCounties = null,
  lookbackMonths = NWSS_DRAWER_CHART_LOOKBACK_MONTHS,
  sampleScope = "historic",
}) => {
  if (panels.length === 0) return null

  const compact = panels.length > 5
  const panelHeight = compact ? 200 : 280

  return (
    <Stack spacing={2}>
      {truncated && (
        <Typography
          variant="caption"
          sx={{ color: extraColors.status.yellowLight }}
        >
          Rolling charts and weekly views may be incomplete — detections are
          prioritized under the sample cap, so the end of the series may be
          biased.
        </Typography>
      )}
      <Box sx={{ maxHeight: compact ? 520 : undefined, overflowY: "auto" }}>
        <Stack spacing={2.5}>
          {panels.map(panel => (
            <RollingRatePanel
              key={panel.geoLabel}
              panel={panel}
              samples={samples}
              disease={disease}
              height={panelHeight}
              reportingCounties={reportingCounties}
              lookbackMonths={lookbackMonths}
              sampleScope={sampleScope}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

interface RollingRatePanelProps {
  panel: NwssRollingDetectionRateData
  samples: NwssSignalSample[]
  disease?: string
  height: number
  reportingCounties: string[] | null
  lookbackMonths: number
  sampleScope: NwssDrawerSampleScope
}

const RollingRatePanel: React.FC<RollingRatePanelProps> = ({
  panel,
  samples,
  disease,
  height,
  reportingCounties,
  lookbackMonths,
  sampleScope,
}) => {
  const useDetectionCount = sampleScope === "event"

  const title = formatNwssRollingRateChartTitle(
    disease,
    panel.geoLabel,
    sampleScope
  )

  const dataset = useMemo(
    () =>
      panel.empty
        ? []
        : panel.points.map(point => ({
            date: dayjsUTC(point.date).toDate(),
            rate: point.ratePct,
            count: point.windowDetectionCount,
          })),
    [panel.empty, panel.points]
  )

  const yAxis = useMemo(
    () =>
      useDetectionCount
        ? computeRollingCountYAxis(panel.points)
        : computeRollingRateYAxis(panel.points),
    [panel.points, useDetectionCount]
  )

  const [hoveredPoint, setHoveredPoint] = useState<{
    index: number
    position: { top: number; left: number }
  } | null>(null)

  const handleMarkHover = useCallback(
    (index: number, event: React.MouseEvent<SVGGElement>) => {
      setHoveredPoint({
        index,
        position: { top: event.clientY, left: event.clientX },
      })
    },
    []
  )

  const handleMarkLeave = useCallback(() => {
    setHoveredPoint(null)
  }, [])

  const MarkSlot = useMemo(
    () =>
      createSampleDateMark({
        onHover: handleMarkHover,
        onLeave: handleMarkLeave,
      }),
    [handleMarkHover, handleMarkLeave]
  )

  if (panel.empty) {
    return (
      <Box>
        <DrawerChartTitle>{title}</DrawerChartTitle>
        <Typography variant="caption" sx={{ color: extraColors.hint }}>
          No samples in this reporting county for the chart window.
        </Typography>
      </Box>
    )
  }

  const activePoint =
    hoveredPoint != null ? panel.points[hoveredPoint.index] : null

  return (
    <Box sx={{ width: "100%" }}>
      <DrawerChartTitle>{title}</DrawerChartTitle>
      <Box sx={{ position: "relative", width: "100%" }}>
        <LineChart
          height={height}
          dataset={dataset}
          series={[
            {
              id: useDetectionCount ? "count" : "rate",
              dataKey: useDetectionCount ? "count" : "rate",
              type: "line",
              showMark: true,
              color: extraColors.medium,
              valueFormatter: (value: number | null) => {
                if (value == null) return ""
                return useDetectionCount
                  ? String(Math.round(value))
                  : `${Math.round(value)}%`
              },
            },
          ]}
          xAxis={[
            {
              dataKey: "date",
              scaleType: "time",
              label: "Sample Collection Date",
              labelStyle: {
                fill: extraColors.translucent50,
                fontSize: 10,
              },
              tickLabelStyle: {
                fill: extraColors.translucent50,
                fontSize: 9,
              },
              valueFormatter: (value: Date) =>
                dayjsUTC(value).format("MMM 'YY"),
            },
          ]}
          yAxis={[
            {
              min: 0,
              max: yAxis.max,
              tickInterval: yAxis.ticks,
              label: nwssRollingChartYAxisLabel(sampleScope),
              labelStyle: {
                fill: extraColors.translucent50,
                fontSize: 10,
              },
              tickLabelStyle: {
                fill: extraColors.translucent50,
                fontSize: 9,
              },
              valueFormatter: (value: number) =>
                useDetectionCount
                  ? String(Math.round(value))
                  : `${Math.round(value)}%`,
            },
          ]}
          grid={{ horizontal: true }}
          margin={{ left: 32, right: 12, top: 12, bottom: 40 }}
          slotProps={{ tooltip: { trigger: "none" } }}
          sx={{
            width: "100%",
            [`.${axisClasses.line}`]: { stroke: extraColors.translucent5 },
            [`.${axisClasses.tick}`]: { stroke: extraColors.translucent5 },
            [`.${axisClasses.left} .${axisClasses.label}`]: {
              transform: "translateX(-4px)",
              transformOrigin: "center center",
            },
          }}
          slots={{ mark: MarkSlot }}
        />
      </Box>
      {activePoint && hoveredPoint && (
        <NwssRollingRateHoverPopper
          open
          anchorPosition={hoveredPoint.position}
          point={activePoint}
          panel={panel}
          samples={samples}
          reportingCounties={reportingCounties}
          lookbackMonths={lookbackMonths}
          sampleScope={sampleScope}
        />
      )}
    </Box>
  )
}

const MARK_HIT_RADIUS = 10

interface SampleDateMarkHandlers {
  onHover: (index: number, event: React.MouseEvent<SVGGElement>) => void
  onLeave: () => void
}

const createSampleDateMark = (handlers: SampleDateMarkHandlers) => {
  const SampleDateMark = (props: MarkElementProps) => (
    <g
      style={{ cursor: "pointer" }}
      onMouseEnter={event => handlers.onHover(props.dataIndex, event)}
      onMouseMove={event => handlers.onHover(props.dataIndex, event)}
      onMouseLeave={handlers.onLeave}
    >
      <circle
        cx={props.x}
        cy={props.y}
        r={MARK_HIT_RADIUS}
        fill="transparent"
      />
      <MarkElement
        {...props}
        style={{
          fill: extraColors.white,
          stroke: extraColors.medium,
          strokeWidth: 1.5,
          transform: "scale(0.9)",
        }}
      />
    </g>
  )
  return SampleDateMark
}
